import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Instagram } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Message sent — mads will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full bg-[#FBF3E2] border border-[rgba(42,33,24,0.12)] focus:border-[#C9501C] outline-none px-5 py-4 text-sm text-[#2A2118] placeholder:text-[#AB9C86] transition-colors";

  return (
    <div className="pt-32 pb-24 px-6 sm:px-12 lg:px-20 min-h-screen" data-testid="contact-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-2xl mx-auto"
      >
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2A2118]">
          Contact
        </h1>
        <p className="mt-6 text-base leading-relaxed text-[#7A6C5A]">
          Questions about a piece, a commission, or shipping — write below and it lands straight in mads' inbox.
        </p>

        <form onSubmit={submit} className="mt-12 space-y-5" data-testid="contact-form">
          <input
            data-testid="contact-name-input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your name"
            className={inputClass}
          />
          <input
            data-testid="contact-email-input"
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email address"
            className={inputClass}
          />
          <textarea
            data-testid="contact-message-input"
            required
            rows={6}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Tell me about the piece you're dreaming of…"
            className={`${inputClass} resize-none`}
          />
          <button
            data-testid="contact-form-submit-button"
            type="submit"
            disabled={sending}
            className="group inline-flex items-center gap-3 bg-[#C9501C] hover:bg-[#f02a4a] disabled:opacity-60 text-[#2A2118] font-mono-accent text-xs uppercase tracking-[0.3em] px-8 py-4 transition-colors duration-300"
          >
            {sending ? "Sending…" : "Send message"}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </form>

        <a
          href="https://www.instagram.com/madsmade47/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-instagram-link"
          className="mt-14 inline-flex items-center gap-3 font-mono-accent text-xs uppercase tracking-[0.3em] text-[#7A6C5A] hover:text-[#C9501C] transition-colors duration-300"
        >
          <Instagram size={16} />
          @madsmade47 on Instagram
        </a>
      </motion.div>
    </div>
  );
}
